import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { eventsApi } from '../../api/events.js';
import { SearchBar } from '../../components/common/SearchBar.jsx';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { EmptyState } from '../../components/common/EmptyState.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { formatDate, todayISO } from '../../utils/dates.js';
import { EVENT_STATUSES, EVENT_STATUS_VARIANT } from '../../utils/constants.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Every date cell (including the leading/trailing days of adjacent months) needed to fill a 6-week grid for the given month. */
function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - start.getDay());
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function EventCalendar({ events }) {
  const today = todayISO();
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const eventsByDate = {};
  for (const e of events) {
    if (!e.date) continue;
    (eventsByDate[e.date] ??= []).push(e);
  }

  const days = buildMonthGrid(cursor.year, cursor.month);
  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const goToMonth = (delta) => {
    setCursor(({ year, month }) => {
      const d = new Date(year, month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="card" style={{ padding: 'var(--space-4)' }}>
      <div className="between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)' }}>{monthLabel}</h2>
        <div className="inline">
          <button className="btn btn-secondary btn-sm" onClick={() => goToMonth(-1)} aria-label="Previous month">‹</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })}>Today</button>
          <button className="btn btn-secondary btn-sm" onClick={() => goToMonth(1)} aria-label="Next month">›</button>
        </div>
      </div>
      <div className="calendar-grid">
        {WEEKDAYS.map((w) => <div key={w} className="calendar-weekday">{w}</div>)}
        {days.map((d) => {
          const iso = toISODate(d);
          const inMonth = d.getMonth() === cursor.month;
          const dayEvents = eventsByDate[iso] ?? [];
          return (
            <div key={iso} className={`calendar-cell${inMonth ? '' : ' outside'}${iso === today ? ' today' : ''}`}>
              <span className="calendar-daynum">{d.getDate()}</span>
              <div className="calendar-events">
                {dayEvents.map((e) => (
                  <Link
                    key={e.id}
                    to={`/events/${e.id}/edit`}
                    className="calendar-event-pill"
                    style={{ borderLeftColor: `var(--color-${EVENT_STATUS_VARIANT[e.status] ?? 'neutral'})` }}
                    title={e.name}
                  >
                    {e.name}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventTable({ events }) {
  return (
    <div className="data-table" style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr><th>Event</th><th>Date</th><th>Format</th><th>Status</th><th>PDU code</th></tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td><Link to={`/events/${e.id}/edit`}>{e.name}</Link></td>
              <td>{formatDate(e.date)}</td>
              <td><span className="badge badge-neutral">{e.format}</span></td>
              <td><StatusBadge status={e.status} type="event" /></td>
              <td style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)' }}>{e.pduCode ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EventListPage() {
  usePageHeader('Events', 'Calendar and full list, side by side');
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    eventsApi.list().then(setEvents).catch(setError).finally(() => setLoading(false));
  }, []);

  const filtered = events.filter((e) => {
    if (status && e.status !== status) return false;
    if (!q) return true;
    return e.name?.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div>
      <div className="page-toolbar">
        <SearchBar value={q} onChange={setQ} placeholder="Search events…" />
        <div className="inline">
          <select className="form-select" style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter">
            <option value="">All statuses</option>
            {EVENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Link to="/events/new" className="btn btn-primary">+ New event</Link>
        </div>
      </div>
      <div className="page-body">
        <ErrorAlert error={error} />
        {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState title="No events" description="Create an event to attach speaker engagements to." action={<Link to="/events/new" className="btn btn-primary">Add event</Link>} />
        ) : (
          <div className="event-calendar-layout">
            <EventCalendar events={filtered} />
            <div>
              <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-3)' }}>All events</h2>
              <EventTable events={filtered} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
